import { Autocomplete, Grid, GridSize, InputAdornment, TextField, Tooltip, Typography } from '@mui/material';
import { Lock } from '@mui/icons-material';
import { useEffect, useRef } from 'react';
import { FormSettingsTypeEnum } from 'src/modules/member/models/formSettingsType.enum';
import { SelectableItem } from 'src/shared/models/gui/selectableItem';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { getPostCode } from 'src/shared/services/addressService';
import ControlledCheckBox from './controlledCheckbox';
import ControlledDatePicker from './controlledDatePicker';
import ControlledMultiCheckBox from './controlledMultiCheckbox';
import ControlledSelectBox from './controlledSelectBox';
import ControlledTextField from './controlledTextField';
import ControlledCheckBoxRadio from './controlledCheckBoxRadio';
import { Colors } from '../styles/colors';

type DynamicFormFieldProps = {
  formItem: any;
  index: number;
  renderFullWidth?: boolean;
};

type PostNrLookupFieldProps = {
  name: string;
  formSetting: { required?: boolean; heading?: string; locked?: boolean };
};

// Postnummer-fält med automatisk uppslagning: när användaren skriver ett giltigt 5-siffrigt
// postnummer hämtas ort/kommunkod/länskod och fylls i motsvarande fält (matchas via formKey).
// Använder Controller direkt (ingen watch på komponentnivå) så att fältet inte re-renderas
// vid varje tangenttryck — annars tappar inputen fokus. Uppslaget triggas av onChange,
// vilket innebär att det bara körs vid användarens inmatning (inte vid laddning).
const PostNrLookupField = ({ name, formSetting }: PostNrLookupFieldProps) => {
  const { control, setValue, getValues } = useFormContext();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastLookedUp = useRef<string>('');

  const runLookup = (raw: string) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    const clean = (raw ?? '').replace(/\s/g, '');
    if (!/^\d{5}$/.test(clean) || clean === lastLookedUp.current) return;

    timerRef.current = setTimeout(() => {
      getPostCode(clean)
        .then((res) => {
          if (!res?.data) return;
          lastLookedUp.current = clean;
          const items = (getValues('items') as Array<{ formKey: string }>) ?? [];
          const setByKey = (key: string, val?: string) => {
            if (val === undefined || val === null) return;
            const idx = items.findIndex((i) => i.formKey === key);
            if (idx >= 0) setValue(`items.${idx}.value`, val, { shouldDirty: true, shouldValidate: true });
          };
          setByKey('postAddress', res.data.ort);
          setByKey('municipalityCode', res.data.kommunkod);
          setByKey('countyCode', res.data.lanskod);
        })
        .catch(() => undefined);
    }, 400);
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <TextField
          {...field}
          value={field.value ?? ''}
          onChange={(e) => {
            field.onChange(e);
            runLookup(e.target.value);
          }}
          fullWidth
          size='small'
          variant='outlined'
          // Fritext (inte type='number') — undviker nummerräknaren, bevarar inledande nollor och tillåter
          // utländska postnummer med bokstäver (t.ex. UK "SW1A 1AA"). Svensk orts-uppslagning triggas ändå
          // bara på 5 siffror, så bokstäver stör inte.
          type='text'
          label={formSetting.heading}
          required={formSetting.required}
          disabled={formSetting.locked}
          InputLabelProps={{ shrink: true }}
          // Låsta fält visar hänglås, samma som övriga fälttyper.
          InputProps={
            formSetting.locked
              ? {
                  endAdornment: (
                    <InputAdornment position='end' sx={{ ml: 0.5 }}>
                      <Lock sx={{ color: 'text.disabled' }} />
                    </InputAdornment>
                  ),
                }
              : undefined
          }
        />
      )}
    />
  );
};

// Kommun-droplista som filtreras efter valt län. Kommunkoden är 4-siffrig och inleds med
// den 2-siffriga länskoden, så en kommun hör till länet om kommunkod.startsWith(länskod).
// Prenumererar ENBART på länets värde via useWatch (inte hela formuläret) så att övriga
// fält – t.ex. postnummer – inte re-renderas och tappar fokus vid varje tangenttryck.
// Rensar även ett tidigare valt kommunval som inte längre hör till det valda länet.
type MunicipalityDropdownFieldProps = {
  fieldName: string;
  formSetting: { heading?: string; required?: boolean; locked?: boolean };
  items: SelectableItem[];
};

const MunicipalityDropdownField = ({ fieldName, formSetting, items }: MunicipalityDropdownFieldProps) => {
  const { control, setValue, getValues } = useFormContext();

  // Fältordningen är statisk efter att formuläret laddats – slå upp index en gång.
  const allItems = (getValues('items') as Array<{ formKey: string }>) ?? [];
  const countyIdx = allItems.findIndex((i) => i.formKey === 'countyCode');
  const municipalityIdx = allItems.findIndex((i) => i.formKey === 'municipalityCode');

  // Smal prenumeration: re-rendera bara när länets värde ändras, inte vid övriga fält.
  const countyValueRaw = useWatch({ control, name: `items.${countyIdx}.value` });
  const countyValue = countyIdx >= 0 ? String(countyValueRaw ?? '') : '';

  // Rensa kommunvalet om det inte hör till valt län.
  useEffect(() => {
    if (!countyValue || municipalityIdx < 0) return;
    const current = String(getValues(`items.${municipalityIdx}.value`) ?? '');
    if (current && !current.startsWith(countyValue)) {
      setValue(`items.${municipalityIdx}.value`, '', { shouldDirty: true, shouldValidate: true });
    }
  }, [countyValue, municipalityIdx, getValues, setValue]);

  const filteredItems = items.filter((o) => o.value === '' || !countyValue || String(o.value).startsWith(countyValue));

  return (
    <ControlledSelectBox
      name={fieldName}
      label={formSetting.heading}
      required={formSetting.required}
      items={filteredItems}
      disabled={formSetting.locked}
    />
  );
};

const DynamicFormField = (props: DynamicFormFieldProps) => {
  const {
    formItem: { formSetting, options, formKey, value }, // formValue not used
    index,
    renderFullWidth = false,
  } = props;

  const { watch, setValue } = useFormContext();

  const fieldName = `items.${index}.value`;
  const formSettingsType = formSetting.type;

  const keyValueArray: SelectableItem[] = [];
  if (options && options.length) {
    options.map((x) => {
      keyValueArray.push({
        value: x.value,
        label: x.name,
      } as SelectableItem);
      return x;
    });
  }

  let returnElement: JSX.Element = <></>;

  if (formSettingsType === FormSettingsTypeEnum.Dropdown) {
    returnElement =
      formKey === 'municipalityCode' ? (
        <MunicipalityDropdownField fieldName={fieldName} formSetting={formSetting} items={keyValueArray} />
      ) : (
        <ControlledSelectBox
          name={fieldName}
          label={formSetting.heading}
          required={formSetting.required}
          items={keyValueArray}
          disabled={formSetting.locked}
        />
      );
  } else if (formSettingsType === FormSettingsTypeEnum.Checkbox) {
    if (!keyValueArray.length) {
      // const isChecked = value === '1';
      returnElement = (
        <ControlledCheckBox
          name={fieldName}
          value={value}
          label={formSetting.heading}
          required={formSetting.required}
          disabled={formSetting.locked}
        />
      );
    } else {
      const isChecked: any[] = [];
      if (!Array.isArray(value)) {
        const valueCopy = [...value.split(',')];
        keyValueArray.forEach((item) => {
          isChecked.push(valueCopy.some((y) => item.value.toString() === y.toString()) ? item.value : null);
        });
      }
      returnElement = (
        <ControlledMultiCheckBox
          name={fieldName}
          value={isChecked || value}
          label={formSetting.heading}
          items={keyValueArray}
          disabled={formSetting.locked} // Not used
          required={formSetting.required} // Not used
        />
      );
    }
  } else if (formSettingsType === FormSettingsTypeEnum.CheckboxRadio) {
    returnElement = (
      <ControlledCheckBoxRadio
        name={fieldName}
        value={value}
        label={formSetting.heading}
        disabled={formSetting.locked}
        required={formSetting.required}
        items={keyValueArray}
      />
    );
  } else if (formSettingsType === FormSettingsTypeEnum.Text || formSettingsType === FormSettingsTypeEnum.Hidden) {
    // || (formSetting.type === FormSettingsTypeEnum.Interested && formSetting.locked)
    returnElement = (
      <ControlledTextField
        name={fieldName}
        label={formSetting.heading}
        disabled={formSetting.locked}
        required={formSetting.required}
        {...(formSettingsType === FormSettingsTypeEnum.Hidden ? { type: 'hidden' } : null)}
        inputProps={{ maxLength: formSetting.extra }}
        {...(formSettingsType === FormSettingsTypeEnum.Hidden ? { type: 'hidden' } : null)}
      />
    );
  }
  //
  /// /
  else if (formSettingsType === FormSettingsTypeEnum.Label || formSettingsType === FormSettingsTypeEnum.Header) {
    // || (formSetting.type === FormSettingsTypeEnum.Interested && formSetting.locked)
    returnElement = <Typography>{formSetting.heading} </Typography>;
  } else if (formSettingsType === FormSettingsTypeEnum.Multiline) {
    returnElement = (
      <ControlledTextField name={fieldName} label={formSetting.heading} required={formSetting.required} multiline rows={formSetting.extra} />
    );
  } else if (formSettingsType === FormSettingsTypeEnum.PostNr) {
    returnElement = <PostNrLookupField name={fieldName} formSetting={formSetting} />;
  } else if (formSettingsType === FormSettingsTypeEnum.Interested) {
    // && !formSetting.locked
    returnElement = <ControlledDatePicker name={fieldName} label={formSetting.heading} disabled={formSetting.locked} />;
  } else if (formSettingsType === FormSettingsTypeEnum.Country) {
    const fieldPath = `items.${index}.value`;
    const formValuePath = `items.${index}.formValue`;

    const uniqueKeyValueArray = Array.from(new Map(keyValueArray.map((item) => [String(item.value), item])).values());
    const currentVal = watch(formValuePath);

    const priorityCountries = ['Sverige', 'Norge', 'Danmark', 'Finland'];

    const sortedOptions = [...uniqueKeyValueArray].sort((a, b) => {
      const aPriority = priorityCountries.indexOf(a.value);
      const bPriority = priorityCountries.indexOf(b.value);

      if (aPriority !== -1 && bPriority !== -1) {
        return aPriority - bPriority;
      }

      if (aPriority !== -1) return -1;
      if (bPriority !== -1) return 1;

      return a.label.localeCompare(b.label, 'sv');
    });

    const selectedOption =
      currentVal && typeof currentVal === 'object' && 'value' in currentVal
        ? currentVal
        : sortedOptions.find((o) => String(o.value) === String(currentVal) || o.label === currentVal) || null;
    const lockAdornment = (
      <InputAdornment position='end' sx={{ ml: 0.5 }}>
        <Lock sx={{ color: 'text.disabled' }} />
      </InputAdornment>
    );

    returnElement = (
      <Autocomplete
        options={sortedOptions}
        value={selectedOption}
        onChange={(_, val) => {
          setValue(fieldPath, val ? val.value : '');
          setValue(formValuePath, val ? val.value : '');
        }}
        getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt.label) || ''}
        isOptionEqualToValue={(opt, val) => String(opt.value) === String((val as any)?.value)}
        disabled={formSetting.locked}
        renderInput={(params) => (
          <TextField
            {...params}
            label={formSetting.heading}
            size='small'
            sx={{
              width: '100%',
              '.MuiInputBase-root': {
                ...(formSetting.locked && { paddingRight: '14px !important' }),
                backgroundColor: formSetting.locked ? Colors.Form.DisabledInput : null,
              },
              ...(formSetting.locked
                ? {
                    '.MuiAutocomplete-endAdornment': {
                      right: 12,
                    },
                  }
                : null),
            }}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              ...params.InputProps,
              endAdornment: formSetting.locked ? lockAdornment : params.InputProps.endAdornment,
            }}
          />
        )}
      />
    );
  } else {
    returnElement = (
      <ControlledTextField name={fieldName} label={formSetting.heading} required={formSetting.required} disabled={formSetting.locked} />
    );
  }

  const getFieldSize = (fieldSize: number, size: 'sm' | 'md'): GridSize => {
    if (size === 'sm') {
      return fieldSize > 1 ? 12 : 6;
    }
    return (fieldSize * 3) as unknown as GridSize;
  };

  const WrapWithTooltip = ({ children }) => {
    // Visa tooltip på låsta fält med errorMessage
    if (formSetting.locked && formSetting.errorMessage) {
      return (
        <Tooltip title={formSetting.errorMessage}>
          <span>{children}</span>
        </Tooltip>
      );
    }
    return children;
  };

  return (
    <>
      {renderFullWidth && (
        <Grid item key={formKey} xs={12}>
          <WrapWithTooltip>{returnElement}</WrapWithTooltip>
        </Grid>
      )}
      {!renderFullWidth && (
        <Grid item key={formKey} xs={12} sm={getFieldSize(formSetting.fieldSize, 'sm')} md={getFieldSize(formSetting.fieldSize, 'md')}>
          <WrapWithTooltip>{returnElement}</WrapWithTooltip>
        </Grid>
      )}
    </>
  );
};

export default DynamicFormField;
